<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-3">
            <h3 class="mb-0">Follow-up campaigns</h3>
            <el-button type="success" @click="openCreate">New campaign</el-button>
        </div>

        <el-alert
            v-if="templatesError"
            :title="templatesError"
            type="warning"
            :closable="false"
            show-icon
            class="mb-3"
        />

        <div class="card">
            <div class="card-body">
                <el-table :data="campaigns" v-loading="loading" empty-text="No follow-up campaigns yet.">
                    <el-table-column prop="name" label="Name" min-width="160" />
                    <el-table-column label="Template" min-width="180">
                        <template #default="{ row }">
                            {{ row.template_name }} ({{ row.template_language }})
                        </template>
                    </el-table-column>
                    <el-table-column label="Schedule" min-width="180">
                        <template #default="{ row }">
                            {{ scheduleLabel(row) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="Status" width="110">
                        <template #default="{ row }">
                            <el-tag :type="row.status === 'active' ? 'success' : 'info'">
                                {{ row.status === 'active' ? 'Active' : 'Inactive' }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="Last run" min-width="170">
                        <template #default="{ row }">
                            <div>{{ row.last_run_at || 'Not run yet' }}</div>
                            <div v-if="row.last_error" class="text-danger">{{ row.last_error }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Sent / failed" width="120">
                        <template #default="{ row }">
                            {{ row.sent_count }} / {{ row.failed_count }}
                        </template>
                    </el-table-column>
                    <el-table-column label="Actions" width="220" fixed="right">
                        <template #default="{ row }">
                            <el-button size="small" @click="openEdit(row)">Edit</el-button>
                            <el-button
                                size="small"
                                :type="row.status === 'active' ? 'warning' : 'success'"
                                @click="toggleStatus(row)"
                            >
                                {{ row.status === 'active' ? 'Deactivate' : 'Activate' }}
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <p v-if="campaignError" class="text-danger mt-2 mb-0">{{ campaignError }}</p>
            </div>
        </div>

        <el-dialog v-model="dialog" :title="form.id ? 'Edit campaign' : 'New campaign'" width="760px" top="4vh">
            <el-form label-position="top" @submit.prevent>
                <el-form-item label="Campaign name" required>
                    <el-input v-model="form.name" maxlength="191" placeholder="Quotation follow-up" />
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="Status">
                            <el-radio-group v-model="form.status">
                                <el-radio label="active">Active</el-radio>
                                <el-radio label="inactive">Inactive</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="Only enquiries with a mobile number">
                            <el-switch v-model="form.require_mobile" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="Internal note">
                    <el-input v-model="form.note" type="textarea" :rows="2" maxlength="2000" placeholder="For the team. This is not sent to the customer." />
                </el-form-item>

                <h5>When to send</h5>
                <p class="text-muted">The message goes out after the enquiry follow-up time, plus this delay. Times use Asia/Kolkata.</p>
                <el-row :gutter="16">
                    <el-col :span="8">
                        <el-form-item label="Delay" required>
                            <el-input-number v-model="form.delay_amount" :min="0" :max="3650" controls-position="right" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="Unit">
                            <el-select v-model="form.delay_unit" style="width: 100%">
                                <el-option label="Minutes" value="minutes" />
                                <el-option label="Hours" value="hours" />
                                <el-option label="Days" value="days" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="Send at (optional)">
                            <el-time-picker
                                v-model="form.send_time"
                                format="HH:mm"
                                value-format="HH:mm"
                                placeholder="As soon as due"
                                style="width: 100%"
                            />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="Enquiry statuses" required>
                    <el-checkbox-group v-model="form.enquiry_statuses">
                        <el-checkbox v-for="status in enquiryStatuses" :key="status" :label="status">{{ status }}</el-checkbox>
                    </el-checkbox-group>
                </el-form-item>

                <h5>Template</h5>
                <el-form-item label="Approved WhatsApp template" required>
                    <el-select
                        v-model="templateKey"
                        filterable
                        placeholder="Select a template"
                        style="width: 100%"
                        @change="onTemplateChange"
                    >
                        <el-option
                            v-for="item in templates"
                            :key="item.name + item.language"
                            :label="item.label"
                            :value="item.name + '||' + item.language"
                        />
                    </el-select>
                </el-form-item>
                <el-alert v-if="preview || buttons.length" type="info" :closable="false" class="mb-3">
                    <pre v-if="preview" class="mb-0" style="white-space: pre-wrap">{{ previewText }}</pre>
                    <div v-if="buttons.length" class="mt-2">Buttons: {{ buttons.join(' · ') }}</div>
                </el-alert>
                <el-form-item v-if="requiresHeaderImage" label="Header image URL" required>
                    <el-input v-model="form.header_image_url" placeholder="https://example.com/package.jpg" />
                    <div class="text-muted">A public https link. The file used to create the template cannot be sent again.</div>
                </el-form-item>

                <div v-if="form.placeholders.length">
                    <h5>Placeholder values</h5>
                    <p class="text-muted">Fixed values are the same for every customer. Enquiry fields are filled when the message is sent.</p>
                    <div v-for="row in form.placeholders" :key="row.key" class="border rounded p-3 mb-2">
                        <strong>{{ row.token }}</strong>
                        <el-row :gutter="12" class="mt-2">
                            <el-col :span="8">
                                <el-select v-model="row.source" style="width: 100%" @change="onSourceChange(row)">
                                    <el-option label="Enquiry field" value="enquiry" />
                                    <el-option label="Fixed value" value="fixed" />
                                </el-select>
                            </el-col>
                            <el-col :span="16">
                                <el-select v-if="row.source === 'enquiry'" v-model="row.value" placeholder="Choose a field" style="width: 100%">
                                    <el-option v-for="field in enquiryFields" :key="field.value" :label="field.label" :value="field.value" />
                                </el-select>
                                <el-input v-else v-model="row.value" type="textarea" :rows="2" maxlength="2000" placeholder="Value sent for this placeholder" />
                            </el-col>
                        </el-row>
                    </div>
                </div>
                <p v-if="formError" class="text-danger">{{ formError }}</p>
            </el-form>
            <template #footer>
                <el-button @click="dialog = false">Cancel</el-button>
                <el-button type="success" :loading="saving" @click="save">Save campaign</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
export default {
    data() {
        return {
            loading: false,
            saving: false,
            dialog: false,
            campaigns: [],
            templates: [],
            templatesError: '',
            enquiryFields: [],
            enquiryStatuses: ['Pending', 'InProgress'],
            templateKey: '',
            preview: '',
            buttons: [],
            requiresHeaderImage: false,
            formError: '',
            campaignError: '',
            form: this.blankForm(),
        };
    },
    computed: {
        previewText() {
            let text = this.preview || '';
            this.form.placeholders.forEach((row) => {
                const replacement = row.source === 'enquiry'
                    ? '[' + (this.fieldLabel(row.value) || 'enquiry field') + ']'
                    : (row.value || row.token);
                text = text.split(row.token).join(replacement);
            });
            return text;
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        blankForm() {
            return {
                id: null,
                name: '',
                status: 'inactive',
                note: '',
                delay_amount: 0,
                delay_unit: 'hours',
                send_time: null,
                enquiry_statuses: ['Pending', 'InProgress'],
                require_mobile: true,
                template_name: '',
                template_language: '',
                header_image_url: '',
                placeholders: [],
            };
        },
        scheduleLabel(row) {
            const delay = row.delay_amount + ' ' + row.delay_unit + ' after follow-up';
            return row.send_time ? delay + ', at ' + row.send_time : delay;
        },
        fieldLabel(value) {
            const match = this.enquiryFields.find((field) => field.value === value);
            return match ? match.label : value;
        },
        load() {
            this.loading = true;
            axios.get('/marketing/followup-campaigns/data')
                .then((response) => {
                    this.campaigns = response.data.campaigns || [];
                    this.templates = response.data.templates || [];
                    this.templatesError = response.data.templates_error || '';
                    this.enquiryFields = response.data.enquiry_fields || [];
                    this.enquiryStatuses = response.data.enquiry_statuses || this.enquiryStatuses;
                })
                .catch((error) => {
                    this.campaignError = this.errorMessage(error, 'Could not load campaigns.');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        openCreate() {
            this.form = this.blankForm();
            this.templateKey = '';
            this.preview = '';
            this.buttons = [];
            this.requiresHeaderImage = false;
            this.formError = '';
            this.dialog = true;
        },
        openEdit(row) {
            this.form = {
                id: row.id,
                name: row.name,
                status: row.status,
                note: row.note || '',
                delay_amount: row.delay_amount,
                delay_unit: row.delay_unit,
                send_time: row.send_time,
                enquiry_statuses: row.enquiry_statuses || [],
                require_mobile: !!row.require_mobile,
                template_name: row.template_name,
                template_language: row.template_language,
                header_image_url: row.header_image_url || '',
                placeholders: JSON.parse(JSON.stringify(row.placeholders || [])),
            };
            this.templateKey = row.template_name + '||' + row.template_language;
            this.preview = '';
            this.buttons = [];
            this.requiresHeaderImage = !!row.header_image_url;
            this.formError = '';
            this.dialog = true;
            this.loadTemplate(true);
        },
        onTemplateChange() {
            this.loadTemplate(false);
        },
        loadTemplate(keepValues) {
            const keep = keepValues === true;
            const parts = (this.templateKey || '').split('||');
            if (parts.length < 2 || !parts[0]) {
                return;
            }
            this.form.template_name = parts[0];
            this.form.template_language = parts[1];
            axios.get('/marketing/followup-campaigns/template', {
                params: { name: parts[0], language: parts[1] },
            }).then((response) => {
                this.preview = response.data.preview || '';
                this.buttons = response.data.buttons || [];
                this.requiresHeaderImage = !!response.data.requires_header_image;
                const existing = {};
                this.form.placeholders.forEach((row) => {
                    existing[row.key] = row;
                });
                this.form.placeholders = (response.data.placeholders || []).map((row) => {
                    if (keep && existing[row.key]) {
                        return {
                            key: row.key,
                            token: row.token,
                            source: existing[row.key].source,
                            value: existing[row.key].value,
                        };
                    }
                    return row;
                });
            }).catch((error) => {
                this.formError = this.errorMessage(error, 'Could not load this template.');
            });
        },
        onSourceChange(row) {
            row.value = row.source === 'enquiry' ? 'name' : '';
        },
        save() {
            this.saving = true;
            this.formError = '';
            const payload = Object.assign({}, this.form);
            delete payload.id;
            const request = this.form.id
                ? axios.put('/marketing/followup-campaigns/' + this.form.id, payload)
                : axios.post('/marketing/followup-campaigns', payload);
            request.then(() => {
                this.dialog = false;
                this.load();
            }).catch((error) => {
                this.formError = this.errorMessage(error, 'Could not save this campaign.');
            }).finally(() => {
                this.saving = false;
            });
        },
        toggleStatus(row) {
            const status = row.status === 'active' ? 'inactive' : 'active';
            this.campaignError = '';
            axios.post('/marketing/followup-campaigns/' + row.id + '/status', { status })
                .then(() => this.load())
                .catch((error) => {
                    this.campaignError = this.errorMessage(error, 'Could not change the campaign status.');
                });
        },
        errorMessage(error, fallback) {
            const data = error.response && error.response.data ? error.response.data : {};
            if (data.errors) {
                const first = Object.keys(data.errors)[0];
                if (first && data.errors[first] && data.errors[first][0]) {
                    return data.errors[first][0];
                }
            }
            return data.message || fallback;
        },
    },
};
</script>
